import { BaseObjDao } from '@/dao';
import { UnknownTag } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import type { TUID } from '@/utilities/typealiases';
import { defineStore, storeToRefs } from 'pinia';
import { useEventStore } from './eventStore';
import { onUnmounted, ref, watch } from 'vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';
import { computed } from 'vue';
import { useContestantTimeStore } from './contestantTimeStore';
import { compareDateTimes } from '@/utilities/dateutils';
import { ProfileHelper } from '@/utilities/profileHelper';
import { watchDebounced } from '@vueuse/core';

interface UnknownTagSubscriptionOptions {
    triggerForExistingResults?: boolean;
}

type LoaderValueType = {
    orderedUnknownTags: UnknownTag[];
    UnknownTags: Map<TUID, UnknownTag>;
};

export const useUnknownTagStore = defineStore('unknownTagStore', () => {
    const eventStore = useEventStore();
    const timeStore = useContestantTimeStore();

    const unseenUnknownTagsCount = ref(0);
    const debouncedUnseenUnknownTagsCount = ref(0);

    watchDebounced(
        unseenUnknownTagsCount,
        (value) => {
            debouncedUnseenUnknownTagsCount.value = value;
        },
        { debounce: 200, maxWait: 1000, immediate: true },
    );

    // set up the subscription to the times, so we can remove the UnknownTags that have a TUID that is in the times, because those tags are not unknown anymore,
    timeStore.SubscribeToContestantTimes(
        (times) => {
            const prof = ProfileHelper.start('UnknownTagStore time subscription');
            prof.mark('subscription callback start');
            // the times has a TUID, we will remove the ones here with same TUID
            const tuidsToRemove = new Set(times.map((t) => t.TUID));
            const deletedUnknownTags: UnknownTag[] = [];
            times.forEach((t) => {
                if (t.TUID && loader.value.Value.UnknownTags.has(t.TUID)) {
                    deletedUnknownTags.push(loader.value.Value.UnknownTags.get(t.TUID)!);
                    loader.value.Value.UnknownTags.delete(t.TUID);
                }
            });
            // only filter if we actually deleted something, otherwise we will trigger a re-render for no reason
            if (deletedUnknownTags.length > 0) {
                loader.value.Value.orderedUnknownTags = loader.value.Value.orderedUnknownTags.filter((gr) => !tuidsToRemove.has(gr.TUID));
                // tell everyone
                UnknownTagDeleteSubscriptions.forEach((cb) => cb(deletedUnknownTags));
            }

            prof.mark('subscription callback end');
            prof.end();
        },
        { triggerForExistingTimes: false },
    );

    const UnknownTagSubscriptions = new Set<(sr: UnknownTag[]) => void>();
    const UnknownTagDeleteSubscriptions = new Set<(sr: UnknownTag[]) => void>();

    const loader = ref(
        new LoaderWithReset<LoaderValueType>({
            defaultValue: {
                UnknownTags: new Map(),
                orderedUnknownTags: [],
            },
            loaderFunction: async () => {
                const UnknownTags = await BaseObjDao.Load<UnknownTag>(UnknownTag, eventStore.eventID);

                const UnknownTagMap = new Map<TUID, UnknownTag>();

                UnknownTags.forEach((gr) => {
                    UnknownTagMap.set(gr.TUID, gr);
                });

                const orderedUnknownTags = UnknownTags.sort((a, b) => compareDateTimes(a.Time, b.Time));

                unseenUnknownTagsCount.value = 0;
                debouncedUnseenUnknownTagsCount.value = 0;

                return {
                    UnknownTags: UnknownTagMap,
                    orderedUnknownTags: orderedUnknownTags,
                };
            },
            waitOnBeforeLoad: eventStore.initpromise,
        }),
    );

    const reset = () => loader.value.Reset();

    const ensureLoaded = async () => await loader.value.Load();

    let signalrUnsubscribeFunction: Function | null = null;
    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        () => eventStore.eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            if (signalrUnsubscribeFunction != null) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.UnknownTag(
                {
                    EventID: eventStore.eventID,
                },
                async (unknownTag) => await handleUnknownTags(unknownTag),
            );
        },
        { immediate: true },
    );

    /**
     * Update and add the new UnknownTags to the store and trigger the subscriptions
     * @param unknownTag
     */
    const handleUnknownTags = (unknownTag: UnknownTag[]) => {
        let newOnes = 0;
        unknownTag.forEach((reading) => {
            // do we already have it?
            if (loader.value.Value.UnknownTags.has(reading.TUID)) {
                return;
            }
            loader.value.Value.UnknownTags.set(reading.TUID, reading);
            newOnes++;

            // We need to add it to the ordered list as well. We do that by looking from the rear and add it after the first reading that is older than it. This is based on the assumption that new readings will usually be newer than the existing ones, so we start from the end of the list
            let addedToOrderedList = false;
            for (let i = loader.value.Value.orderedUnknownTags.length - 1; i >= 0; i--) {
                if (loader.value.Value.orderedUnknownTags[i].Time.getTime() <= reading.Time.getTime()) {
                    loader.value.Value.orderedUnknownTags.splice(i + 1, 0, reading);
                    addedToOrderedList = true;

                    break;
                }
            }
            if (!addedToOrderedList) {
                loader.value.Value.orderedUnknownTags.unshift(reading);
            }
        });

        unseenUnknownTagsCount.value += newOnes;

        UnknownTagSubscriptions.forEach((cb) => cb(unknownTag));
    };

    /**
     * Set up the subscription for UnknownTag. The callback will be triggered every time we get a new reading and if triggerForExistingResults is true, it will also be triggered with the existing readings
     * @param callback
     * @param options
     */
    const subscribeToUnknownTag = (callback: (sr: UnknownTag[]) => void, options?: UnknownTagSubscriptionOptions) => {
        UnknownTagSubscriptions.add(callback);

        // If we should trigger for existing values, do so now
        if (options?.triggerForExistingResults) {
            ensureLoaded().then(() => {
                callback(Array.from(loader.value.Value.UnknownTags.values()));
            });

            callback(Array.from(loader.value.Value.UnknownTags.values()));
        }

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            UnknownTagSubscriptions.delete(callback);
        };
    };

    /**
     * Set up the subscription for UnknownTag delete events. The callback will be triggered every time we get a deleted UnknownTag
     * @param callback
     */
    const subscribeToUnknownTagDelete = (callback: (sr: UnknownTag[]) => void) => {
        UnknownTagDeleteSubscriptions.add(callback);

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            UnknownTagDeleteSubscriptions.delete(callback);
        };
    };

    return {
        /**
         * Ensure that all the data will be loaded from the server
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Subscribe to UnknownTag. The callback will be invoked every time we get a new UnknownTag
         */
        SubscribeToUnknownTag: subscribeToUnknownTag,

        /**
         * Subscribe to UnknownTag delete events. The callback will be invoked every time we get a deleted UnknownTag
         */
        SubscribeToUnknownTagDelete: subscribeToUnknownTagDelete,

        ResetSeenCount: () => {
            unseenUnknownTagsCount.value = 0;
            debouncedUnseenUnknownTagsCount.value = 0;
        },

        UnseenUnknownTagsCount: computed(() => debouncedUnseenUnknownTagsCount.value),

        /**
         * A list of all the UnknownTags ordered by time.
         * @returns {UnknownTag[]}
         */
        OrderedUnknownTags: computed(() => {
            return loader.value.Value.orderedUnknownTags;
        }),
    };
});

export const useUnknownTagStoreRef = () => storeToRefs(useUnknownTagStore());

// helper composable to handle lifetime of a subscription to the unknownTagStore
export function useUnknownTagSubscription(callback: (sr: UnknownTag[]) => void, options?: UnknownTagSubscriptionOptions) {
    const store = useUnknownTagStore();

    const cleanup = store.SubscribeToUnknownTag(callback, options);

    onUnmounted(() => {
        cleanup();
    });
}

// helper composable to handle lifetime of a subscription to the unknownTagStore
export function useUnknownTagDeleteSubscription(callback: (sr: UnknownTag[]) => void) {
    const store = useUnknownTagStore();

    const cleanup = store.SubscribeToUnknownTagDelete(callback);

    onUnmounted(() => {
        cleanup();
    });
}
